<template>
  <v-form ref="form" lazy-validation>
    <v-container fluid class="pa-0">
      <v-row>
        <v-col v-if="isRecognition" cols="12" sm="6" md="4">
          <v-text-field
            dense
            outlined
            v-model="formData.ExaminingBody"
            :rules="rules.ExaminingBody"
            @input="upper('ExaminingBody', $event)"
            ref="ExaminingBody"
          >
            <template v-slot:label>
              <div>Examining Body <span class="red--text">*</span></div>
            </template>
          </v-text-field>
        </v-col>
        <v-col v-else cols="12" sm="6" md="4">
          <v-text-field
            dense
            outlined
            v-model="formData.Institution"
            :rules="rules.Institution"
            @input="upper('Institution', $event)"
            ref="Institution"
          >
            <template v-slot:label>
              <div>Institution <span class="red--text">*</span></div>
            </template>
          </v-text-field>
        </v-col>

        <v-col v-if="isProfessional" cols="12" sm="6" md="4">
          <v-text-field
            dense
            outlined
            v-model="formData.AreaOfSpecialization"
            :rules="rules.AreaOfSpecialization"
            ref="AreaOfSpecialization"
            messages="e.g Bachelor of Arts"
          >
            <template v-slot:label>
              <div>
                Academic Qualification
                <span class="red--text">*</span>
              </div>
            </template>
          </v-text-field>
        </v-col>

        <v-col cols="12" sm="6" md="4">
          <v-select
            dense
            outlined
            label="Certificate Awarded"
            :items="certificationCodes"
            v-model="formData.CertificateAwarded"
            :rules="rules.CertificateAwarded"
            ref="CertificateAwarded"
            item-value="Code"
            item-text="Description"
          >
            <template v-slot:label>
              <div>
                Certificate Awarded
                <span class="red--text">*</span>
              </div>
            </template>
          </v-select>
        </v-col>

        <v-col v-if="isRecognition" cols="12" sm="6" md="4">
          <v-select
            dense
            outlined
            label="Qualification"
            :items="qualificationCodes"
            v-model="formData.QualificationCode"
            ref="QualificationCode"
            item-value="Code"
            item-text="Description"
          >
            <template v-slot:label>
              <div>
                Qualification
                <span class="red--text">*</span>
              </div>
            </template>
          </v-select>
        </v-col>

        <v-col cols="12" sm="6" md="4">
          <DatePickerInput
            v-model="formData.StartDate"
            label="Start Date"
            :rules="rules.StartDate"
            required
          />
        </v-col>

        <v-col v-if="isEducational" cols="12" sm="6" md="4">
          <DatePickerInput
            v-model="formData.EndDate"
            label="End Date"
            :rules="rules.EndDate"
            required
          />
        </v-col>

        <v-col v-if="isProfessional || isRecognition" cols="12" sm="6" md="4">
          <DatePickerInput
            v-model="formData.GraduationDate"
            label="Graduation Date"
            :rules="rules.GraduationDate"
            required
          />
        </v-col>

        <v-col cols="12" sm="6" md="4">
          <v-text-field
            dense
            outlined
            v-model="formData.CertificateNo"
            :rules="rules.CertificateNo"
            ref="CertificateNo"
            @input="upper('CertificateNo', $event)"
          >
            <template v-slot:label>
              <div>Certificate No. <span class="red--text">*</span></div>
            </template>
          </v-text-field>
        </v-col>

        <v-col cols="12" sm="6" md="4">
          <v-file-input
            outlined
            dense
            ref="File"
            accept="application/pdf"
            hint="pdf documents only"
            persistent-hint
            type="file"
            v-model="formData.File"
            :rules="rules.File"
            :append-outer-icon="formData.Cert ? 'mdi-download' : ''"
            @click:append-outer="download(formData.Cert)"
          >
            <template v-slot:label>
              <div>
                Attach Certified Certificate
                <span class="red--text">*</span>
              </div>
            </template>
          </v-file-input>
        </v-col>
      </v-row>

      <TranscriptSection
        v-if="isForeign"
        class="mt-3"
        v-model="formData.transcript"
        @download="download"
      />

      <ForeignContactSection
        v-if="isForeign"
        v-model="formData"
        :rules="rules"
        :countries="countries"
        class="mt-3"
      />
      <v-row>
        <v-col cols="12" class="d-flex justify-end">
          <v-btn text @click="close"> Cancel</v-btn>

          <v-btn color="primary" @click="save">
            Save
            <v-icon right> mdi-checkbox-marked-circle</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
  </v-form>
</template>

<script>
import moment from "moment/moment";
import DatePickerInput from "@/components/inputs/DatePickerInput.vue";
import TranscriptSection from "@/modules/profile/components/certification/components/TranscriptSection.vue";
import ForeignContactSection from "@/modules/profile/components/certification/components/ForeignContactSection.vue";

export default {
  name: "CertificationForm",
  components: { ForeignContactSection, TranscriptSection, DatePickerInput },
  props: {
    minorSettings: Object,
    certificationCodes: Array,
    qualificationCodes: Array,
    countries: Array,
  },
  data() {
    return {
      formData: this.defaultCertification(),
    };
  },

  computed: {
    rules() {
      return {
        Institution: [(v) => !!v || "Institution is required"],
        ExaminingBody: [(v) => !!v || "Examining Body is required"],
        AreaOfSpecialization: [
          (v) => !!v || "Area of Specialization is required",
        ],
        CertificateAwarded: [(v) => !!v || "Certificate Awarded is required"],
        QualificationCode: [(v) => !!v || "Qualification is required"],
        Type: [(v) => !!v || "Type is required"],
        CertificateNo: [(v) => !!v || "Certificate No is required"],
        Category: [(v) => !!v || "Category is required"],
        StartDate: [(v) => !!v || "Start Date is required"],
        EndDate: [
          (v) => !!v || "End Date is required",
          (v) =>
            moment(this.formData.StartDate).isBefore(moment(v)) ||
            "End Date Cannot come before Start Date",
        ],
        GraduationDate: [
          (v) => !!v || "Graduation Date is required",
          (v) =>
            moment(this.formData.StartDate).isBefore(moment(v)) ||
            "Graduation Date Cannot come before Start Date",
        ],
        UniversityCode: [
          (v) =>
            ((this.formData.Category === "Local" ||
              this.formData.Category === "Foreign") &&
              !!v) ||
            "University is required",
        ],
        ContactPerson: [
          (v) =>
            (this.formData.Category === "Foreign" && !!v) ||
            "Contact Person is required",
        ],
        ContactDesignation: [
          (v) =>
            (this.formData.Category === "Foreign" && !!v) ||
            "Designation is required",
        ],
        ContactEmail: [
          (v) =>
            (this.formData.Category === "Foreign" && !!v) ||
            "Email is required",
          (v) => /.+@.+/.test(v) || "E-mail must be valid",
        ],
        contactPersonAddress: [
          (v) =>
            (this.formData.Category === "Foreign" && !!v) ||
            "Address is required",
        ],
        ContactCountry: [
          (v) =>
            (this.formData.Category === "Foreign" && !!v) ||
            "Country is required",
        ],
        File: [
          (v) => {
            return this.formData.Cert === ""
              ? !!v || "Attachment is required"
              : true;
          },

          (v) => {
            return v
              ? (!!v && v.size < 10000000) ||
                  "Attachment should be less than 10MB"
              : true;
          },

          (v) => {
            return v
              ? (!!v && v.type.includes("application/pdf")) ||
                  "Attachment must be a Pdf"
              : true;
          },
        ],
      };
    },

    isForeign() {
      return this.formData.Category === "Foreign";
    },

    isEducational() {
      return this.formData.Type === "Educational";
    },

    isProfessional() {
      return this.formData.Type === "Professional";
    },

    isRecognition() {
      return this.formData.Type === "Recognition";
    },
  },

  methods: {
    defaultCertification() {
      return {
        Institution: "",
        AreaOfSpecialization: "",
        CertificateAwarded: "",
        Type: "",
        CertificateNo: "",
        Category: "",
        StartDate: "",
        EndDate: "",
        GraduationDate: "",
        ContactPerson: "",
        ContactDesignation: "",
        contactPersonAddress: "",
        ContactEmail: "",
        ContactCountry: "",
        UniversityCode: "",
        File: null,
        Cert: "",
        transcript: [],
        ExaminingBody: "",
        QualificationCode: "",
      };
    },

    async validate() {
      const valid = this.$refs.form.validate();

      return Boolean(valid);
    },

    reset() {
      this.formData = this.defaultCertification();

      this.$nextTick(() => {
        this.$refs.form.resetValidation();
      });
    },

    setData(data) {
      this.formData = {
        ...this.defaultCertification(),
        ...data,
      };
    },

    async save() {
      const isValid = await this.validate();

      if (!isValid) return;
      this.$emit("save", this.formData);
    },

    close() {
      this.$emit("close");
    },

    upper(field, value) {
      this.formData[field] = value ? value.toUpperCase() : "";
    },
    download(file) {
      this.$emit("download", file);
    },
  },
};
</script>

<style scoped></style>
